import {StyleSheet, css} from 'aphrodite';
import moment from 'moment';
import React from 'react';

import {trackEvent} from 'analytics/Analytics';
import {SEARCH_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import LLMFeedbackModal from 'llm/feedback/LLMFeedbackModal';
import {isLLMFeedbackEnabled} from 'llm/feedback/LLMFeedbackUtils';
import * as ToastActions from 'nav/toasts/ToastActions';
import {ToastType} from 'nav/toasts/ToastConstants';
import Notifications from 'notifications';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {ClientSearchConstants, inferSearchParams} from 'search/ai-assisted-search/AIAssistedSearchDAO';
import AIAssistedSearchModal from 'search/ai-assisted-search/AIAssistedSearchModal';
import {getFilterKeysForModelType} from 'search/FiltersByType';
import {NOT_FILTERABLE_FIELD_TYPES, operatorsForFieldType, replaceByKey} from 'search/FilterUtil';
import {FILTER_KEY_TO_OPERATORS, FilterKey, SearchParams} from 'search/SearchConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import runModal from 'ui-library/runModal';
import {CANONICAL_FORMAT} from 'util/DateConstants';
import {withLoadingScreen} from 'util/Promises';

/**
 * Return an object of constants useful for the backend to enforce search params constraints and
 * describe them to the LLM.
 */
function collectSearchConstants(availableTypes: Array<string>): ClientSearchConstants {
  return {
    filterKeysByItemType: Object.fromEntries(
      availableTypes.map((type) => [type, getFilterKeysForModelType(type)])
    ),
    operatorsByFilterKey: FILTER_KEY_TO_OPERATORS,
    operatorsByFieldType: Object.fromEntries(
      Object.values(FieldType)
        .filter((fieldType) => !NOT_FILTERABLE_FIELD_TYPES.includes(fieldType))
        .map((fieldType) => [fieldType, operatorsForFieldType(fieldType)])
    ),
  };
}

export interface AIAssistedSearchButtonProps {
  availableTypes: Array<string>;
  availableFilterKeys: Array<FilterKey>;
  /**
   * Search params with all user-specifiable options cleared. We always extend this, e.g. in a
   * folder listing we keep the specified folder and other filters core to the UI, and allow
   * AI-specified options for the rest.
   */
  baseSearchParams: SearchParams;
  onSearch: (searchParams: SearchParams) => void;
  onSetSecondaryDropdownOpen: (isSecondaryDropdownOpen: boolean) => void;
}

/**
 * Guided search button and corresponding logic. If the modal is accepted and the AI system
 * determines search params, we update the user's params to that.
 */
export default function AIAssistedSearchButton({
  availableTypes,
  availableFilterKeys,
  baseSearchParams,
  onSearch,
  onSetSecondaryDropdownOpen,
}: AIAssistedSearchButtonProps): JSX.Element | null {
  async function handleClick(): Promise<void> {
    trackEvent(SEARCH_ANALYTICS_ACTIONS.OPENED_AI_ASSISTED_SEARCH_MODAL);
    const modalResult = await runModal(AIAssistedSearchModal, {});
    if (modalResult) {
      const userTime = moment(new Date().toISOString()).format(CANONICAL_FORMAT);
      const start = Date.now();
      const inferSearchParamsResponse = await withLoadingScreen(
        inferSearchParams(
          modalResult.userPrompt,
          availableTypes,
          availableFilterKeys,
          userTime,
          collectSearchConstants(availableTypes)
        ),
        'Determining search settings...'
      );
      const elapsed = Date.now() - start;
      if (inferSearchParamsResponse.searchParams) {
        onSearch({
          ...baseSearchParams,
          query: inferSearchParamsResponse.searchParams.query,
          filters: replaceByKey(baseSearchParams.filters, ...inferSearchParamsResponse.searchParams.filters),
        });
        onSetSecondaryDropdownOpen(true);
        if (inferSearchParamsResponse.filterErrors && inferSearchParamsResponse.filterErrors.length > 0) {
          // Show a toast of filter errors if any were sent back. Some notes:
          // * It's somewhat awkward that we show two toasts, but they're currently hard to combine
          //   into one. This is a bit of a stopgap until the information is shown in the modal.
          // * The toast does not give much space, so we phrase things concisely and give a bit more
          //   room via styling.
          Notifications.warn(
            <div>
              Some filters had errors and were skipped:
              <ul className={css(styles.filterErrorList)}>
                {inferSearchParamsResponse.filterErrors.map((error, i) => (
                  <li key={i}>{error}</li>
                ))}
              </ul>
            </div>,
            {duration: 20}
          );
        }
        if (isLLMFeedbackEnabled()) {
          ToastActions.pushToast({
            message: "AI configured search settings. How'd it do?",
            duration: 12,
            type: ToastType.INFO,
            actions: [
              {
                actionText: '👍',
                onActionClick: () => {
                  trackEvent(SEARCH_ANALYTICS_ACTIONS.SUBMITTED_AI_ASSISTED_SEARCH_THUMBSUP, {
                    traceId: inferSearchParamsResponse.traceId,
                  });
                  ToastActions.pushToast({
                    message: 'Thanks for the feedback!',
                    duration: 3,
                    type: ToastType.SUCCESS,
                  });
                },
              },
              {
                actionText: '👎',
                onActionClick: () => {
                  trackEvent(SEARCH_ANALYTICS_ACTIONS.SUBMITTED_AI_ASSISTED_SEARCH_THUMBSDOWN, {
                    traceId: inferSearchParamsResponse.traceId,
                  });
                  ToastActions.pushToast({
                    message: 'Thanks for the feedback!',
                    duration: 5,
                    type: ToastType.INFO,
                    actions: [
                      {
                        actionText: 'Tell us more',
                        onActionClick: () => {
                          onSetSecondaryDropdownOpen(false);
                          runModal(LLMFeedbackModal, {
                            title: 'Guided search feedback',
                            optionalContextItems: [{name: 'Search query', value: modalResult.userPrompt}],
                            requiredContextItems: [
                              {
                                name: 'Latency',
                                value: `${elapsed.toLocaleString()}ms`,
                              },
                              {
                                name: 'Trace ID',
                                value: inferSearchParamsResponse.traceId,
                              },
                            ],
                          });
                        },
                      },
                    ],
                  });
                },
              },
            ],
          });
        }
      } else {
        Notifications.error(inferSearchParamsResponse.error ?? 'Unable to determine search settings.');
      }
    }
  }

  return (
    <Button
      icon="magic-wand"
      text="Guided search"
      size={ButtonSize.SMALL}
      buttonStyle={ButtonStyle.TERTIARY_GRAY}
      onClick={handleClick}
    />
  );
}

const styles = StyleSheet.create({
  filterErrorList: {
    marginTop: 2,
    marginBottom: 0,
    paddingLeft: 20,
  },
});
